/**
 * Captaincy Statistics Filtering and Sorting Utilities
 *
 * Business logic for filtering and sorting captaincy records.
 * Extracted from presentational component to enable testing and reusability.
 */

import type { CaptaincyRecord } from './captaincy.utils';

export type CaptaincyViewMode = 'record' | 'due';

/**
 * Filter captaincy records based on view mode
 * @param records - Array of captaincy records
 * @param viewMode - View mode (record or due)
 * @param hasCurrentTeamData - Whether current team data is available
 * @returns Filtered array of captaincy records
 */
export const filterCaptaincyRecords = (
  records: CaptaincyRecord[],
  viewMode: CaptaincyViewMode,
  hasCurrentTeamData: boolean
): CaptaincyRecord[] => {
  // In "Due Captaincy" mode, filter to only show current team members if we have team data
  if (viewMode === 'due' && hasCurrentTeamData) {
    return records.filter((record) => record.isInCurrentTeam);
  }

  return records;
};

/**
 * Sort captaincy records based on view mode
 * @param records - Array of captaincy records
 * @param viewMode - View mode (record or due)
 * @returns Sorted array of captaincy records
 */
export const sortCaptaincyRecords = (records: CaptaincyRecord[], viewMode: CaptaincyViewMode): CaptaincyRecord[] => {
  return [...records].sort((a, b) => {
    if (viewMode === 'due') {
      // Current captains (upcoming event) should sort last (they have 0 appearances since captaincy)
      // For current captains, treat as 0 for sorting purposes
      // Use the count that includes the upcoming event
      const aValue = a.isCurrentCaptain ? 0 : a.appearancesSinceLastCaptaincyIncludingUpcoming;
      const bValue = b.isCurrentCaptain ? 0 : b.appearancesSinceLastCaptaincyIncludingUpcoming;

      // Sort by appearances since last captaincy (descending - most appearances first)
      return bValue - aValue;
    }

    // Default 'record' mode: already sorted by container (times as captain desc, then name)
    return 0;
  });
};

/**
 * Filter and sort captaincy records in one operation
 * @param records - Array of captaincy records
 * @param viewMode - View mode (record or due)
 * @param hasCurrentTeamData - Whether current team data is available
 * @returns Filtered and sorted array of captaincy records
 */
export const filterAndSortCaptaincyRecords = (
  records: CaptaincyRecord[],
  viewMode: CaptaincyViewMode,
  hasCurrentTeamData: boolean
): CaptaincyRecord[] => {
  const filtered = filterCaptaincyRecords(records, viewMode, hasCurrentTeamData);
  return sortCaptaincyRecords(filtered, viewMode);
};
